import { createFileRoute } from "@tanstack/react-router";
import { useLanguage } from "@/i18n/LanguageProvider";
import { User, Calendar, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/blog")({
  head: () => ({ meta: [{ title: "Blog — Code Cubs Academy" }] }),
  component: BlogPage,
});

function BlogPage() {
  const { t } = useLanguage();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPosts() {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("created_at", { ascending: false });
      setPosts(data || []);
      setLoading(false);
    }
    fetchPosts();
  }, []);

  return (
    <div className="bg-background text-foreground min-h-screen">
      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl lg:text-6xl font-extrabold mb-4">المدونة</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          مقالات ونصائح قيمة للآباء عن التعليم والتكنولوجيا ومستقبل أطفالنا.
        </p>
      </section>

      <section className="container mx-auto px-4 pb-24">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">لا توجد مقالات حالياً.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article
                key={post.id}
                className="bg-surface border border-surface-elevated rounded-3xl overflow-hidden hover:border-primary/50 transition-all group flex flex-col"
              >
                <div className="h-56 overflow-hidden relative">
                  <img
                    src={
                      post.cover_image_url ||
                      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=800"
                    }
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 start-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                    تعليم
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                    <div className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      <span>{post.author_name || "Admin"}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{new Date(post.created_at).toLocaleDateString("ar-EG")}</span>
                    </div>
                  </div>
                  <h2 className="text-xl font-bold leading-snug mb-3 group-hover:text-primary transition-colors">
                    <a href={`/blog/${post.slug}`}>{post.title}</a>
                  </h2>
                  <p className="text-sm leading-relaxed text-foreground/80 line-clamp-3 mb-6 flex-1">
                    {post.excerpt}
                  </p>
                  <div className="pt-4 border-t border-surface-elevated mt-auto">
                    <a
                      href={`/blog/${post.slug}`}
                      className="text-primary font-bold text-sm hover:underline"
                    >
                      اقرأ المزيد &larr;
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
